import layout from './Filter.html?raw';
import AsyncControl from '@/Controls/AsyncControl';
import Link from '@/Controls/Link';
import ComponentManager from '@/Management/ComponentManager';
import Config from '@/Management/GlobalConfiguration';

import TextBoxType from '@/Controls/TextBox';
import FilterViewModelType from '@/ViewModels/Controls/FilterViewModel';

import FilterElementType from '@/Controls/FilterElement';
import FilterElementViewModelType from '@/ViewModels/Controls/FilterElementViewModel';

import FilterSortElementType from '@/Controls/FilterSortElement';
import FilterSortElementViewModelType from '@/ViewModels/Controls/FilterSortElementViewModel';

import LocatorHelper from '@/Utility/LocatorHelper';

export default class Filter extends AsyncControl {

    protected jQueryControl: JQuery;
    private elements: Array<FilterElementType | FilterSortElementType>;
    private isComponentReady: KnockoutObservable<boolean>;

    public constructor(private viewModel: FilterViewModelType, private element?: JQuery) {
        super(viewModel, element);
        this.isComponentReady = ko.observable(false);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public async getElementAsync(): Promise<JQuery> {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        // das FilterControl soll initial nicht angezeigt werden, wenn keine aktiven Filter und keine FilterChips vorhanden sind
        if (_.isEmpty(this.viewModel.elements()) && _.isEmpty(this.viewModel.filterChipsViewModel.values())) {
            let defered = $.Deferred();

            // wenn das FilterControl initial nicht angezeigt werden soll kann es angezeigt werden, indem das VM auf visible gesetzt wird
            // es kann auch sein, dass die FilterChips später geladen werden, wegen Asynchronität
            const shouldRenderComputed = this.defineComputed(() => {
                const chips = this.viewModel.filterChipsViewModel.values();
                const isFilterVisible = this.viewModel.isVisible();
                return !_.isEmpty(chips) || isFilterVisible;
            })
            this.subscribeUntil(shouldRenderComputed, (shouldRender) => {
                if (shouldRender) {
                    defered.resolve();
                    shouldRenderComputed.dispose();
                }
                return shouldRender;
            });
            await defered;
        }

        this.jQueryControl = this.element || $('<div>');

        const timerId = this.applyTemplate(layout, this.jQueryControl);
        this.triggers[timerId].then(() => {
            if (this.isDisposed)
                return;

            this.widget(null);
            this.isComponentReady(true);
        });

        this.elements = [];

        return this.jQueryControl;
    }

    protected onWidgetCreatedCallback(widget: null): void {
        const $chipsArea = this.jQueryControl.children().find('.chips-area');
        const $filterArea = this.jQueryControl.children().find('.filter-area');
        const $sortArea = this.jQueryControl.children().find('.sort-area');
        const $saveFilterArea = this.jQueryControl.children().find('.save-filter-area');
        const $sortAndFilterContainer = this.jQueryControl.children().find('.sort-and-filter-container');

        this.applyBindings(this.jQueryControl, {
            controlName: 'filter',
            locator: this.viewModel.locator
        });

        this.applyBindings($sortAndFilterContainer, {
            visible: this.viewModel.isVisible
        });

        this.applyBindings($filterArea, {
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            tooltip: this.viewModel.tooltip,
            invalid: this.viewModel.invalidationText,
            visible: this.viewModel.isVisible,
            styles: this.definePureComputed({
                read: () => {
                    var styles = [];

                    return styles;
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];
                    if (this.viewModel.isRequired()) {
                        states.push('required');
                    }
                    return states;
                }
            })
        });

        this.renderSaveFilterTextBox();

        this.applyBindings($filterArea, {
            foreach: {
                data: this.viewModel.elements,
                afterRender: (elements: Array<HTMLDivElement>, filterElementViewModel: FilterElementViewModelType) => {
                    this.renderFilterRow(filterElementViewModel, elements);

                    const $allConnectors = this.getAllConnectors();
                    const $lastConnector = $allConnectors.last();

                    $allConnectors.show();
                    $lastConnector.hide();
                },
                beforeRemove: (domNode: HTMLDivElement, index: number, element: FilterElementViewModelType) => {
                    const $domNode = $(domNode);
                    const $siblingsAndSelf = $domNode.parent().children();
                    const isLast = $siblingsAndSelf.index($domNode) === $siblingsAndSelf.length - 1;

                    if (isLast && $domNode.siblings().length >= 1) {
                        $domNode.siblings().last().find('.connectors').hide();
                    }

                    $(domNode).remove();
                }
            },
            childrenComplete: () => {
                // ViewModel benachritigen, wenn alle FilterElemente gerenderd sind 
                this.viewModel.isFilterLoadingDeactivated(false);
            }
        });

        this.applyBindings($saveFilterArea, {
            visible: this.defineComputed(() => {
                return this.viewModel.shouldShowSaveInputs() && this.viewModel.isVisible();
            })
        });

        // render sort area
        this.applyBindings($sortArea, {
            foreach: {
                data: this.viewModel.sortElements,
                afterRender: (elements: Array<HTMLDivElement>, sortElementViewModel: FilterSortElementViewModelType) => {
                    this.renderSortRow(sortElementViewModel, elements);
                }
            },
            visible: this.viewModel.shouldShowSorting
        });

        this.applyBindings($chipsArea, {
            locator: LocatorHelper.buildLocator(this.viewModel.locator, 'chipsarea')
        });
    }

    private renderSaveFilterTextBox(): void {
        const TextBox = ComponentManager.getControl<typeof TextBoxType>('TextBox');
        const $textBoxElement = this.jQueryControl.children().find('.save-filter-textbox');
        $textBoxElement.append('<input>');
        const textBoxControl = new TextBox(this.viewModel.filterNameTextBoxViewModel, $textBoxElement);
        
        const $linkElement = this.jQueryControl.children().find('.save-filter-link');
        const linkControl = new Link(this.viewModel.filterSaveLinkViewModel, $linkElement);

        linkControl.getControl();
        textBoxControl.getControl();
    }

    private getAllConnectors(): JQuery<HTMLElement> {
        return this.jQueryControl.children().find('.connectors');
    }

    private renderFilterRow(filterElementViewModel: FilterElementViewModelType, elements: Array<HTMLDivElement>) {
        const filterElement = this.renderFilterElement(filterElementViewModel, _.last(elements));
        filterElement.getControl();

        this.elements.push(filterElement);
    }

    private renderFilterElement(filterElementViewModel, element): FilterElementType {
        const FilterElement = ComponentManager.getControl<typeof FilterElementType>('FilterElement');
        const filterElement = new FilterElement(filterElementViewModel, $(element));
        filterElement.getTuple();

        return filterElement;
    }

    private renderSortRow(sortElementViewModel: FilterSortElementViewModelType, elements: Array<HTMLDivElement>) {
        const sortElement = this.renderSortElement(sortElementViewModel, _.last(elements));
        sortElement.getControl();

        this.elements.push(sortElement);
    }

    private renderSortElement(sortElementViewModel, element): FilterSortElementType {
        const FilterSortElement = ComponentManager.getControl<typeof FilterSortElementType>('FilterSortElement');
        const sortElement = new FilterSortElement(sortElementViewModel, $(element));
        sortElement.getTuple();

        return sortElement;
    }
}

ComponentManager.setControl('Filter', Filter, 'RADPresentation.HTMLClient.DefaultViewExtensions');